/**
 * Cross-Filter Manager for Dashboard Widgets
 * Enables Power BI-like cross-filtering between FusionCharts widgets.
 */

class CrossFilterManager {
	constructor() {
		this.filters = new Map();
		this.chartRegistry = new Map();
		this.listeners = new Set();
		this.sourceChartId = null;
		this.isUpdating = false;
		this.debounceTimeout = null;
		this.highlightStyles = {
			selectedAlpha: "100",
			unselectedAlpha: "30",
			sourceColor: null, // Use original color for source
			relatedColor: null // Use original color for related
		};

		this.highlightMode = "auto";
		this.seriesFilter = null;
		// The data field name (e.g. "project_type") of the clicked category axis.
		// Stored so card widgets can re-fetch with the correct filter key.
		this.crossFilterFieldName = null;
		// Raw (un-formatted) value from the source chart's data field, used for filter injection.
		// Distinct from the display label stored in filters.get("category").
		this.crossFilterRawValue = null;
		// Store dark mode provider function to get current state
		this.getDarkModeState = null;
	}

	/**
	 * Set the dark mode state provider function
	 * @param {function} providerFn - Function that returns { isDark: boolean }
	 */
	setDarkModeProvider(providerFn) {
		this.getDarkModeState = providerFn;
	}

	/**
	 * Get dark mode chart overrides (shared between apply and reset)
	 * @returns {object} Dark mode FusionCharts property overrides
	 */
	_getDarkOverrides(options = {}) {
		// For transparent containers, use the dashboard background color instead of card color
		const bgColor = (options.isTransparent && options.dashboardBgColor)
			? options.dashboardBgColor
			: '#17121F';
		return {
			bgColor: bgColor,
			bgAlpha: '100',
			canvasBgColor: bgColor,
			canvasBgAlpha: '100',
			canvasBorderColor: '#221B2E',
			showCanvasBorder: '0',
			baseFontColor: '#d1d5db',
			divLineColor: '#221B2E',
			divLineAlpha: '60',
			showAlternateHGridColor: '0',
			showAlternateVGridColor: '0',
			vDivLineColor: '#221B2E',
			vDivLineAlpha: '40',
			xAxisLineColor: '#4b5563',
			yAxisLineColor: '#4b5563',
			xAxisNameFontColor: '#9ca3af',
			yAxisNameFontColor: '#9ca3af',
			labelFontColor: '#9ca3af',
			yAxisValueFontColor: '#9ca3af',
			sYAxisNameFontColor: '#9ca3af',
			toolTipBgColor: '#17121F',
			toolTipBorderColor: '#4b5563',
			toolTipFontColor: '#f3f4f6',
			toolTipBgAlpha: '97',
			legendBgColor: bgColor,
			legendBgAlpha: '0',
			legendBorderColor: '#221B2E',
			legendBorderAlpha: '0',
			legendItemFontColor: '#d1d5db',
			legendItemHoverFontColor: '#f3f4f6',
			legendCaptionFontColor: '#d1d5db',
			crossLineColor: '#6b7280',
			crossLineAlpha: '30',
			trendlineColor: '#6b7280',
			valueFontColor: '#d1d5db',
			plotBorderColor: bgColor,
			scrollColor: '#221B2E',
			exportBtnColor: '#9ca3af',
			exportBtnBgColor: bgColor
		};
	}

	/**
	 * Set the highlight mode for multi-series charts
	 * @param {string} mode - "category", "series", "both", or "auto"
	 */
	setHighlightMode(mode) {
		const validModes = ["category", "series", "both", "auto"];
		if (validModes.includes(mode)) {
			this.highlightMode = mode;
		}
	}

	/**
	 * Get the current highlight mode
	 * @returns {string}
	 */
	getHighlightMode() {
		return this.highlightMode;
	}

	/**
	 * Configure highlight styles
	 * @param {object} styles - Custom highlight styles
	 */
	setHighlightStyles(styles) {
		this.highlightStyles = { ...this.highlightStyles, ...styles };
	}

	/**
	 * Register a chart with the cross-filter system
	 * @param {string} chartId - Unique chart ID (FusionCharts ID)
	 * @param {string} widgetId - Widget ID from dashboard
	 * @param {string[]} dimensions - Array of dimension names this chart can filter on
	 * @param {object} chartInstance - FusionCharts instance reference
	 * @param {object} originalData - Original chart data for reset
	 * @param {boolean} enableCrossFilter - Whether cross-filtering is enabled for this chart
	 */
	registerChart(chartId, widgetId, dimensions, chartInstance, originalData, enableCrossFilter = true, metadata = {}, xSeriesDimension = null) {
		if (!chartId || !chartInstance) {
			console.warn("CrossFilterManager: Invalid chart registration - missing chartId or chartInstance");
			return;
		}

		const extractedDimensions = dimensions || this.extractDimensions(originalData);
		let storedOriginalData = this.deepClone(originalData);
		
		console.log(`CrossFilterManager: Registering chart ${chartId}`, {
			hasDataset: !!storedOriginalData?.dataset,
			datasetLength: storedOriginalData?.dataset?.length,
			hasNestedDataset: storedOriginalData?.dataset?.[0]?.dataset ? true : false,
			hasLineset: !!storedOriginalData?.lineset,
			hasData: !!storedOriginalData?.data,
			hasCategories: !!storedOriginalData?.categories,
			enableCrossFilter,
			isTransparent: metadata.isTransparent,
			dashboardBgColor: metadata.dashboardBgColor
		});
		
		this.chartRegistry.set(chartId, {
			widgetId,
			dimensions: extractedDimensions,
			chartInstance,
			originalData: storedOriginalData,
			enableCrossFilter,
			isTransparent: !!metadata.isTransparent,
			dashboardBgColor: metadata.dashboardBgColor || null,
			xSeriesDimension: xSeriesDimension || null
		});
		
		console.log(`CrossFilterManager: Registered chart ${chartId} with dimensions:`, extractedDimensions, `crossFilter enabled: ${enableCrossFilter}`);
	}

	/**
	 * Update cross-filter enabled state for a chart
	 * @param {string} chartId - Chart ID
	 * @param {boolean} enabled - Whether cross-filtering is enabled
	 */
	setCrossFilterEnabled(chartId, enabled) {
		const chartInfo = this.chartRegistry.get(chartId);
		if (chartInfo) {
			chartInfo.enableCrossFilter = enabled;
			this.chartRegistry.set(chartId, chartInfo);
			
			if (enabled) {
				this.setupChartEventListener(chartId, chartInfo.chartInstance);
			} else {
				// Older implementations attempted to remove an internal listener reference here,
				// but FusionCharts throws a ParameterException when an undefined handler is passed.
				// Since our widgets attach their own dataplotClick handlers and call the manager
				// explicitly, we only need to reset the chart visuals.
				this.resetChartToOriginal(chartId, chartInfo);
			}
		}
	}

	/**
	 * Unregister a chart from the cross-filter system
	 * @param {string} chartId - Chart ID to unregister
	 */
	unregisterChart(chartId) {
		// Avoid calling FusionCharts.removeEventListener with an undefined handler, which
		// triggers "ParameterException: removeListener Error >> Invalid Event Listener".
		// Chart instances are being destroyed by React, so we only need to clear registry state.
		this.chartRegistry.delete(chartId);
		console.log(`CrossFilterManager: Unregistered chart ${chartId}`);
	}

	/**
	 * Set up dataplotClick event listener on a chart
	 * @param {string} chartId - Chart ID
	 * @param {object} chartInstance - FusionCharts instance
	 */
	setupChartEventListener(chartId, chartInstance) {
		if (!chartInstance || typeof chartInstance.addEventListener !== "function") {
			console.warn(`CrossFilterManager: Cannot add event listener to chart ${chartId}`);
			return;
		}

		const manager = this;

		try {
			chartInstance.removeEventListener("dataplotClick", this.handleDataplotClick);
		} catch (e) {}


		chartInstance.addEventListener("dataplotClick", function(eventObj, dataObj) {
			manager.onDataplotClick(chartId, eventObj, dataObj);
		});
	}

	/**
	 * Handle dataplot click event
	 * @param {string} chartId - Source chart ID
	 * @param {object} eventObj - FusionCharts event object
	 * @param {object} dataObj - Clicked data point information
	 */
	onDataplotClick(chartId, eventObj, dataObj) {
		if (this.isUpdating) return;

		console.log("CrossFilterManager: dataplotClick event", { chartId, dataObj });

		const { categoryLabel, datasetName, value, dataIndex, datasetIndex, displayValue, toolText, link } = dataObj;

		const filterValue = categoryLabel || displayValue || value;
		const dimension = "category";
		const seriesName = datasetName || dataObj.seriesName || null;

		if (!filterValue) {
			console.warn("CrossFilterManager: No filter value found in clicked data point", dataObj);
			return;
		}

		console.log("CrossFilterManager: Filter extracted:", { filterValue, seriesName, mode: this.highlightMode });

		// Check if clicking same filter value - toggle off
		const currentCategoryFilter = this.filters.get(dimension);
		const isSameCategoryFilter = currentCategoryFilter === filterValue;
		const isSameSeriesFilter = this.seriesFilter === seriesName;
		
		// Toggle behavior based on highlight mode
		if (this.highlightMode === "category" && isSameCategoryFilter) {
			this.clearFilters();
			return;
		}
		if (this.highlightMode === "series" && isSameSeriesFilter && !currentCategoryFilter) {
			this.clearFilters();
			return;
		}
		if (this.highlightMode === "both" && isSameCategoryFilter && isSameSeriesFilter) {
			this.clearFilters();
			return;
		}
		if (this.highlightMode === "auto" && isSameCategoryFilter && (isSameSeriesFilter || !seriesName)) {
			this.clearFilters();
			return;
		}

		// Look up the source chart's registered xSeriesDimension so downstream widgets
		// can decide whether to re-fetch (same field â†’ alpha-dim) or update their data.
		const sourceChartInfo = this.chartRegistry.get(chartId);
		const fieldName = sourceChartInfo?.xSeriesDimension || null;

		// Set the filter (with series info for multi-series charts)
		this.setFilterWithSeries(dimension, filterValue, seriesName, chartId, fieldName, filterValue);
	}

	/**
	 * Set a filter with series information for multi-series charts
	 * @param {string} dimension - Dimension name to filter on (e.g., "category")
	 * @param {*} value - Filter value (e.g., "Jan")
	 * @param {string} seriesName - Series name for multi-series filtering (e.g., "Online Sales")
	 * @param {string} sourceChartId - ID of chart that initiated the filter
	 * @param {string} fieldName - Underlying data field name (e.g. "project_type"); used by card widgets to re-fetch
	 */
	setFilterWithSeries(dimension, value, seriesName = null, sourceChartId = null, fieldName = null, rawValue = null) {
		if (this.isUpdating) return;

		this.isUpdating = true;
		this.sourceChartId = sourceChartId;
		this.seriesFilter = seriesName;
		this.crossFilterFieldName = fieldName;
		this.crossFilterRawValue = rawValue !== null && rawValue !== undefined ? rawValue : null;
		
		this.filters.clear();
		this.filters.set(dimension, value);
		
		if (seriesName) {
			this.filters.set("series", seriesName);
		}

		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout);
		}

		this.debounceTimeout = setTimeout(() => {
			this.resetAllChartsToOriginal();
			setTimeout(() => {
				this.applyFiltersToAllCharts();
				this.notifyListeners();
				this.isUpdating = false;
			}, 10);
		}, 50);
	}

	/**
	 * Set a filter and apply to all related charts
	 * @param {string} dimension - Dimension name to filter on
	 * @param {*} value - Filter value
	 * @param {string} sourceChartId - ID of chart that initiated the filter
	 * @param {string} fieldName - Underlying data field name; used by card widgets to re-fetch
	 */
	setFilter(dimension, value, sourceChartId = null, fieldName = null, rawValue = null) {
		this.setFilterWithSeries(dimension, value, null, sourceChartId, fieldName, rawValue);
	}

	/**
	 * Set multiple filters at once
	 * @param {object} filters - Object with dimension: value pairs
	 * @param {string} sourceChartId - ID of chart that initiated the filter
	 */
	setMultipleFilters(filters, sourceChartId = null) {
		if (this.isUpdating) return;

		this.isUpdating = true;
		this.sourceChartId = sourceChartId;

		Object.entries(filters).forEach(([dimension, value]) => {
			this.filters.set(dimension, value);
		});

		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout);
		}

		this.debounceTimeout = setTimeout(() => {
			this.applyFiltersToAllCharts();
			this.notifyListeners();
			this.isUpdating = false;
		}, 50);
	}

	/**
	 * Clear a specific filter
	 * @param {string} dimension - Dimension to clear
	 */
	clearFilter(dimension) {
		this.filters.delete(dimension);
		if (dimension === "series") {
			this.seriesFilter = null;
		}
		this.sourceChartId = null;
		this.applyFiltersToAllCharts();
		this.notifyListeners();
	}

	/**
	 * Clear all filters and reset charts to original state
	 */
	clearFilters() {
		this.isUpdating = true;
		this.filters.clear();
		this.sourceChartId = null;
		this.seriesFilter = null;
		this.crossFilterFieldName = null;

		this.resetAllChartsToOriginal();

		this.notifyListeners();
		this.isUpdating = false;
	}

	/**
	 * Reset all charts to their original state (stored at registration)
	 */
	resetAllChartsToOriginal() {
		this.chartRegistry.forEach((chartInfo, chartId) => {
			this.resetChartToOriginal(chartId, chartInfo);
		});
	}

	/**
	 * Reset a specific chart to its original state (stored at registration)
	 * @param {string} chartId - Chart ID
	 * @param {object} chartInfo - Chart registration info
	 */
	resetChartToOriginal(chartId, chartInfo) {
		const { chartInstance, originalData, enableCrossFilter, isTransparent, dashboardBgColor } = chartInfo;
		
		if (!chartInstance || !originalData) return;
		if (!enableCrossFilter) return;
		// Skip if FusionCharts instance has been disposed (e.g. during React unmount)
		if (chartInstance.disposed || typeof chartInstance.setJSONData !== 'function') return;

		try {
			// Clone the original data to get the unfiltered datasets
			const originalClone = this.deepClone(originalData);

			// Get the CURRENT chart config from the live instance to preserve
			// margins, responsive sizing, and any runtime layout properties
			let currentChart = originalClone.chart || {};
			try {
				const liveData = chartInstance.getJSONData();
				if (liveData && liveData.chart) {
					currentChart = { ...liveData.chart };
				}
			} catch (_) { /* fall back to stored chart config */ }

			// Build the data source with current chart config + original data
			const dataSource = {
				...originalClone,
				chart: currentChart
			};
			
			// Apply current dark mode state to chart config
			// Pass transparency info so transparent charts keep the dashboard bg color
			if (this.getDarkModeState && dataSource.chart) {
				const { isDark } = this.getDarkModeState();
				if (isDark) {
					dataSource.chart = { ...dataSource.chart, ...this._getDarkOverrides({ isTransparent, dashboardBgColor }) };
				}
			}
			
			// Restore the original data with current dark mode state
			chartInstance.setJSONData(dataSource);
		} catch (error) {
			console.error(`CrossFilterManager: Error resetting chart ${chartId} to original:`, error);
		}
	}

	/**
	 * Apply current filters to all registered charts
	 */
	applyFiltersToAllCharts() {
		if (this.filters.size === 0) {
			this.resetAllChartsToOriginal();
			return;
		}

		this.chartRegistry.forEach((chartInfo, chartId) => {
			if (!chartInfo.enableCrossFilter) return;
			
			this.applyFilterToChart(chartId, chartInfo);
		});
	}

	/**
	 * Apply filter to a specific chart
	 * @param {string} chartId - Chart ID
	 * @param {object} chartInfo - Chart registration info
	 */
	applyFilterToChart(chartId, chartInfo) {
		const { chartInstance, originalData, dimensions, enableCrossFilter, isTransparent, dashboardBgColor } = chartInfo;
		
		if (!chartInstance) {
			console.warn(`CrossFilterManager: No chart instance for ${chartId}`);
			return;
		}
		
		if (!enableCrossFilter) return;

		// When crossFilterFieldName is set the click came from a Recharts chart.
		// Widgets whose query supports the field will re-fetch (overriding any alpha-dim),
		// while widgets that don't support it stay alpha-dimmed â€” so always apply alpha here.
		// Skip only non-source charts when there is no field name (pure FusionCharts path),
		// since the source chart should always show the highlight on its selected plot.

		const isSourceChart = chartId === this.sourceChartId;
		const filterValue = this.filters.get("category");

		console.log(`CrossFilterManager: Applying filter "${filterValue}" to chart ${chartId}`, {
			isSourceChart,
			hasDataset: !!originalData?.dataset,
			datasetLength: originalData?.dataset?.length,
			hasNestedDataset: originalData?.dataset?.[0]?.dataset ? true : false,
			hasLineset: !!originalData?.lineset,
			hasData: !!originalData?.data,
			hasCategories: !!originalData?.categories,
			categoriesLength: originalData?.categories?.[0]?.category?.length,
			isTransparent,
			dashboardBgColor
		});

		try {
			const originalClone = this.deepClone(originalData);
			if (!originalClone) {
				console.warn(`CrossFilterManager: No dataSource for chart ${chartId}`);
				return;
			}

			// Get the CURRENT chart config from the live instance to preserve
			// margins, responsive sizing, and any runtime layout properties
			let currentChart = originalClone.chart || {};
			try {
				const liveData = chartInstance.getJSONData();
				if (liveData && liveData.chart) {
					currentChart = { ...liveData.chart };
				}
			} catch (_) { /* fall back to stored chart config */ }

			// Build data source with current chart config + original data
			const dataSource = {
				...originalClone,
				chart: currentChart
			};

			// Apply current dark mode state to chart config
			// Pass transparency info so transparent charts keep the dashboard bg color
			if (this.getDarkModeState && dataSource.chart) {
				const { isDark } = this.getDarkModeState();
				if (isDark) {
					dataSource.chart = { ...dataSource.chart, ...this._getDarkOverrides({ isTransparent, dashboardBgColor }) };
				}
			}

			// For non-source charts: attempt smart filtering (show only the matched
			// series/category) before falling back to alpha-based highlighting.
			let finalDataSource;
			if (!isSourceChart) {
				const smartFiltered = this._trySmartFilter(dataSource, filterValue);
				finalDataSource = smartFiltered !== null ? smartFiltered : this.highlightDataSource(dataSource, filterValue, false);
			} else {
				finalDataSource = this.highlightDataSource(dataSource, filterValue, true);
			}

			if (chartInstance && typeof chartInstance.setJSONData === 'function') {
				chartInstance.setJSONData(finalDataSource);
			} else {
				console.warn(`CrossFilterManager: Cannot call setJSONData for ${chartId}`, chartInstance);
			}

		} catch (error) {
			console.error(`CrossFilterManager: Error applying filter to chart ${chartId}:`, error);
		}
	}

	/**
	 * Attempt to filter a chart's data to show only the data matching filterValue.
	 * Tries series-name match first, then category match, then single-series label match.
	 * Returns a filtered dataSource clone when a match is found, or null when no match
	 * exists (caller should fall back to alpha-highlight behavior).
	 * @param {object} dataSource - FusionCharts data source
	 * @param {string} filterValue - Value selected on the source chart
	 * @returns {object|null}
	 */
	_trySmartFilter(dataSource, filterValue) {
		if (!dataSource || !filterValue) return null;

		const cloned = this.deepClone(dataSource);

		// 1. Series-name match on flat multi-series dataset
		if (cloned.dataset && Array.isArray(cloned.dataset) && cloned.dataset[0]?.dataset === undefined) {
			const matchingSeries = cloned.dataset.filter(series => {
				const name = series.seriesname || series.seriesName || "";
				return this.valueMatches(name, filterValue);
			});
			if (matchingSeries.length > 0) {
				cloned.dataset = matchingSeries;
				return cloned;
			}
		}

		// 2. Category match on charts with a categories array
		const categories = cloned.categories?.[0]?.category || [];
		if (categories.length > 0) {
			const matchingIdx = this.findCategoryIndex(categories, filterValue);
			if (matchingIdx !== -1) {
				const catEntry = { ...cloned.categories[0], category: [categories[matchingIdx]] };
				cloned.categories = [catEntry];

				if (cloned.dataset && Array.isArray(cloned.dataset)) {
					cloned.dataset = cloned.dataset.map(series => {
						if (series.data && Array.isArray(series.data)) {
							return { ...series, data: [series.data[matchingIdx]] };
						}
						return series;
					});
				}

				if (cloned.lineset && Array.isArray(cloned.lineset)) {
					cloned.lineset = cloned.lineset.map(line => {
						if (line.data && Array.isArray(line.data)) {
							return { ...line, data: [line.data[matchingIdx]] };
						}
						return line;
					});
				}

				return cloned;
			}
		}

		// 3. Single-series label match
		if (cloned.data && Array.isArray(cloned.data)) {
			const matchingData = cloned.data.filter(item =>
				this.valueMatches(item.label, filterValue) ||
				this.valueMatches(item.name, filterValue) ||
				this.valueMatches(item.category, filterValue)
			);
			if (matchingData.length > 0 && matchingData.length < cloned.data.length) {
				cloned.data = matchingData;
				return cloned;
			}
		}

		return null; // No structural match â€” caller falls back to alpha-highlight
	}

	/**
	 * Highlight data source based on filter value
	 * @param {object} dataSource - FusionCharts data source
	 * @param {string} filterValue - Category value to highlight
	 * @param {boolean} isSource - Whether this is the source chart
	 * @returns {object} - Highlighted data source
	 */
	highlightDataSource(dataSource, filterValue, isSource) {
		const clonedSource = this.deepClone(dataSource);
		const { selectedAlpha, unselectedAlpha } = this.highlightStyles;
		
		const seriesFilterValue = this.filters.get("series") || this.seriesFilter;
		const mode = this.highlightMode;

		const categories = clonedSource.categories?.[0]?.category || [];
		
		console.log("CrossFilterManager: highlightDataSource", {
			filterValue,
			seriesFilterValue,
			mode,
			categoriesCount: categories.length,
			hasData: !!clonedSource.data,
			hasDataset: !!clonedSource.dataset,
			hasLineset: !!clonedSource.lineset
		});

		// Handle single-series charts (data array)
		if (clonedSource.data && Array.isArray(clonedSource.data)) {
			clonedSource.data = clonedSource.data.map(item => {
				const matches = this.valueMatches(item.label, filterValue) || 
				               this.valueMatches(item.name, filterValue) ||
				               this.valueMatches(item.category, filterValue);
				
				// Only modify alpha - preserve all original styling (borders, colors, etc.)
				return {
					...item,
					alpha: matches ? selectedAlpha : unselectedAlpha
				};
			});
		}

		// Handle multi-series, combo, and stacked charts (dataset array with categories)
		// MULTI-SERIES: dataset[seriesIndex].data[categoryIndex]
		// STACKED: dataset[groupIndex].dataset[seriesIndex].data[categoryIndex]
		// COMBO: dataset[seriesIndex].data[categoryIndex] + lineset[lineIndex].data[categoryIndex]
		if (clonedSource.dataset && Array.isArray(clonedSource.dataset)) {
			clonedSource.dataset = this.highlightDatasetWithMode(
				clonedSource.dataset, 
				categories, 
				filterValue,
				seriesFilterValue,
				mode,
				selectedAlpha, 
				unselectedAlpha
			);
		}

		// COMBO CHARTS: Handle lineset for dual-axis charts (column+line combinations)
		// The lineset contains secondary axis data that must be highlighted
		// at the same category index as the primary dataset
		if (clonedSource.lineset && Array.isArray(clonedSource.lineset)) {
			clonedSource.lineset = this.highlightLineset(
				clonedSource.lineset,
				categories,
				filterValue,
				seriesFilterValue,
				mode,
				selectedAlpha,
				unselectedAlpha
			);
		}

		// Handle category array for nested pie/sunburst charts
		if (clonedSource.category && Array.isArray(clonedSource.category)) {
			clonedSource.category = this.highlightCategoryArray(clonedSource.category, filterValue, selectedAlpha, unselectedAlpha);
		}

		return clonedSource;
	}

	/**
	 * Highlight lineset for dual-axis/combo charts
	 * @param {array} lineset - Lineset array
	 * @param {array} categories - Categories for index matching
	 * @param {string} categoryFilter - Category value to filter
	 * @param {string} seriesFilter - Series name to filter
	 * @param {string} mode - Highlight mode
	 * @param {string} selectedAlpha - Alpha for matching items
	 * @param {string} unselectedAlpha - Alpha for non-matching items
	 * @returns {array} - Highlighted lineset
	 */
	highlightLineset(lineset, categories, categoryFilter, seriesFilter, mode, selectedAlpha, unselectedAlpha) {
		const matchingCategoryIndex = this.findCategoryIndex(categories, categoryFilter);
		
		return lineset.map(lineSeries => {
			if (lineSeries.data && Array.isArray(lineSeries.data)) {
				const seriesName = lineSeries.seriesname || lineSeries.seriesName || "";
				const seriesMatches = this.valueMatches(seriesName, seriesFilter);
				
				return {
					...lineSeries,
					data: lineSeries.data.map((item, index) => {
						// Determine if this data point matches based on mode
						const categoryMatches = matchingCategoryIndex !== -1 
							? index === matchingCategoryIndex
							: this.valueMatches(categories[index]?.label, categoryFilter) ||
							  this.valueMatches(item.label, categoryFilter);
						
						const matches = this.determineMatch(
							categoryMatches, 
							seriesMatches, 
							mode, 
							categoryFilter, 
							seriesFilter
						);
						
						return {
							...item,
							alpha: matches ? selectedAlpha : unselectedAlpha
						};
					})
				};
			}
			return lineSeries;
		});
	}

	/**
	 * Highlight dataset array with support for different modes
	 * @param {array} dataset - Dataset array
	 * @param {array} categories - Categories for index matching
	 * @param {string} categoryFilter - Category value to filter
	 * @param {string} seriesFilter - Series name to filter
	 * @param {string} mode - Highlight mode: "category", "series", "both", "auto"
	 * @param {string} selectedAlpha - Alpha for matching items
	 * @param {string} unselectedAlpha - Alpha for non-matching items
	 * @returns {array} - Highlighted dataset
	 */
	highlightDatasetWithMode(dataset, categories, categoryFilter, seriesFilter, mode, selectedAlpha, unselectedAlpha) {
		const matchingCategoryIndex = this.findCategoryIndex(categories, categoryFilter);
		
		console.log("CrossFilterManager: highlightDatasetWithMode", { 
			categoryFilter,
			seriesFilter,
			mode,
			matchingCategoryIndex,
			categoriesCount: categories?.length,
			datasetLength: dataset?.length,
			hasNestedDataset: dataset?.[0]?.dataset ? true : false
		});
		
		return dataset.map(series => {
			const result = { ...series };
			const seriesName = series.seriesname || series.seriesName || "";
			const seriesMatches = this.valueMatches(seriesName, seriesFilter);
			
			if (series.dataset && Array.isArray(series.dataset)) {
				result.dataset = this.highlightDatasetWithMode(
					series.dataset, 
					categories, 
					categoryFilter,
					seriesFilter,
					mode,
					selectedAlpha, 
					unselectedAlpha
				);
			}
			
			if (series.data && Array.isArray(series.data)) {
				result.data = series.data.map((item, index) => {
					let categoryMatches = false;
					if (matchingCategoryIndex !== -1) {
						categoryMatches = index === matchingCategoryIndex;
					} else {
						const categoryLabel = categories[index]?.label || "";
						categoryMatches = this.valueMatches(categoryLabel, categoryFilter);
					}
					
					if (!categoryMatches) {
						categoryMatches = this.valueMatches(item.label, categoryFilter) ||
						                 this.valueMatches(item.name, categoryFilter) ||
						                 this.valueMatches(item.x, categoryFilter);
					}
					
					// Determine final match based on mode
					const matches = this.determineMatch(
						categoryMatches, 
						seriesMatches, 
						mode, 
						categoryFilter, 
						seriesFilter
					);
					
					return {
						...item,
						alpha: matches ? selectedAlpha : unselectedAlpha
					};
				});
			}
			
			return result;
		});
	}

	/**
	 * Determine if a data point matches based on the highlight mode
	 * @param {boolean} categoryMatches - Whether the category matches
	 * @param {boolean} seriesMatches - Whether the series matches
	 * @param {string} mode - Highlight mode
	 * @param {string} categoryFilter - Category filter value
	 * @param {string} seriesFilter - Series filter value
	 * @returns {boolean} - Whether this data point should be highlighted
	 */
	determineMatch(categoryMatches, seriesMatches, mode, categoryFilter, seriesFilter) {
		const hasCategoryFilter = !!categoryFilter;
		const hasSeriesFilter = !!seriesFilter;
		
		switch (mode) {
			case "category":
				return categoryMatches;
				
			case "series":
				return seriesMatches;
				
			case "both":
				if (hasCategoryFilter && hasSeriesFilter) {
					return categoryMatches && seriesMatches;
				} else if (hasCategoryFilter) {
					return categoryMatches;
				} else if (hasSeriesFilter) {
					return seriesMatches;
				}
				return false;
				
			case "auto":
			default:
				if (hasCategoryFilter && hasSeriesFilter) {
					return categoryMatches;
				} else if (hasCategoryFilter) {
					return categoryMatches;
				} else if (hasSeriesFilter) {
					return seriesMatches;
				}
				return false;
		}
	}

	/**
	 * Recursively highlight dataset array
	 * @param {array} dataset - Dataset array
	 * @param {array} categories - Categories for index matching
	 * @param {string} filterValue - Value to filter
	 * @param {string} selectedAlpha - Alpha for matching items
	 * @param {string} unselectedAlpha - Alpha for non-matching items
	 * @returns {array} - Highlighted dataset
	 */
	highlightDataset(dataset, categories, filterValue, selectedAlpha, unselectedAlpha) {
		const matchingCategoryIndex = this.findCategoryIndex(categories, filterValue);
		
		console.log("CrossFilterManager: highlightDataset", { 
			filterValue,
			matchingCategoryIndex,
			categoriesCount: categories?.length,
			datasetLength: dataset?.length,
			hasNestedDataset: dataset?.[0]?.dataset ? true : false
		});
		
		return dataset.map(series => {
			const result = { ...series };
			
			if (series.dataset && Array.isArray(series.dataset)) {
				result.dataset = this.highlightDataset(
					series.dataset, 
					categories, 
					filterValue, 
					selectedAlpha, 
					unselectedAlpha
				);
			}
			
			if (series.data && Array.isArray(series.data)) {
				result.data = series.data.map((item, index) => {
					let matches = false;
					
					if (matchingCategoryIndex !== -1) {
						matches = index === matchingCategoryIndex;
					} else {
						const categoryLabel = categories[index]?.label || "";
						matches = this.valueMatches(categoryLabel, filterValue);
					}
					
					if (!matches) {
						matches = this.valueMatches(item.label, filterValue) ||
						         this.valueMatches(item.name, filterValue) ||
						         this.valueMatches(item.x, filterValue);
					}
					
					return {
						...item,
						alpha: matches ? selectedAlpha : unselectedAlpha
					};
				});
			}
			
			return result;
		});
	}

	/**
	 * Find the index of a category by its label
	 * @param {array} categories - Categories array from chart data
	 * @param {string} filterValue - The category label to find
	 * @returns {number} - Index of the matching category, or -1 if not found
	 */
	findCategoryIndex(categories, filterValue) {
		if (!categories || !Array.isArray(categories) || !filterValue) {
			return -1;
		}
		
		return categories.findIndex(cat => 
			this.valueMatches(cat.label, filterValue) || 
			this.valueMatches(cat.name, filterValue)
		);
	}

	/**
	 * Recursively highlight category array
	 * @param {array} categoryArray - Category array
	 * @param {string} filterValue - Value to filter
	 * @param {string} selectedAlpha - Alpha for matching items
	 * @param {string} unselectedAlpha - Alpha for non-matching items
	 * @returns {array} - Highlighted category array
	 */
	highlightCategoryArray(categoryArray, filterValue, selectedAlpha, unselectedAlpha) {
		return categoryArray.map(item => {
			const result = { ...item };
			
			const matches = this.valueMatches(item.label, filterValue) ||
			               this.valueMatches(item.name, filterValue);
			
			result.alpha = matches ? selectedAlpha : unselectedAlpha;
			
			if (item.category && Array.isArray(item.category)) {
				result.category = this.highlightCategoryArray(item.category, filterValue, selectedAlpha, unselectedAlpha);
			}
			
			return result;
		});
	}

	/**
	 * Decode HTML entities in a string
	 * @param {string} str - String with potential HTML entities
	 * @returns {string} - Decoded string
	 */
	decodeHTMLEntities(str) {
		if (!str || typeof str !== 'string') return str;
		
		// Common HTML entities mapping
		const entities = {
			'&#039;': "'",
			'&#39;': "'",
			'&apos;': "'",
			'&quot;': '"',
			'&#034;': '"',
			'&#34;': '"',
			'&amp;': '&',
			'&#038;': '&',
			'&#38;': '&',
			'&lt;': '<',
			'&#060;': '<',
			'&#60;': '<',
			'&gt;': '>',
			'&#062;': '>',
			'&#62;': '>',
			'&nbsp;': ' ',
			'&#160;': ' '
		};
		
		let decoded = str;
		for (const [entity, char] of Object.entries(entities)) {
			decoded = decoded.split(entity).join(char);
		}
		
		return decoded;
	}

	/**
	 * Check if a value matches the filter value (case-insensitive)
	 * @param {*} value - Value to check
	 * @param {*} filterValue - Filter value to match
	 * @returns {boolean}
	 */
	valueMatches(value, filterValue) {
		if (value === undefined || value === null || filterValue === undefined || filterValue === null) {
			return false;
		}
		const strValue = this.decodeHTMLEntities(String(value)).toLowerCase().trim();
		const strFilter = this.decodeHTMLEntities(String(filterValue)).toLowerCase().trim();
		return strValue === strFilter;
	}

	/**
	 * Reset a chart to its original state
	 * @param {string} chartId - Chart ID
	 * @param {object} chartInfo - Chart registration info
	 */
	resetChart(chartId, chartInfo) {
		const { chartInstance, originalData } = chartInfo;
		
		if (!chartInstance || !originalData) return;

		try {
			// Get current data and remove highlighting
			const dataSource = this.getChartDataSource(chartInstance);
			if (!dataSource) return;

			const resetDataSource = this.removeHighlighting(dataSource);
			chartInstance.setJSONData(resetDataSource);

		} catch (error) {
			console.error(`CrossFilterManager: Error resetting chart ${chartId}:`, error);
		}
	}

	/**
	 * Remove highlighting from data source
	 * @param {object} dataSource - FusionCharts data source
	 * @returns {object} - Data source without highlighting
	 */
	removeHighlighting(dataSource) {
		// Simply return the data as-is since we're not adding any styling properties
		// Original borders, colors, and other styling are preserved
		return this.deepClone(dataSource);
	}

	/**
	 * Get data source from chart instance
	 * @param {object} chartInstance - FusionCharts instance
	 * @returns {object|null} - Data source or null
	 */
	getChartDataSource(chartInstance) {
		try {
			const jsonData = chartInstance.getJSONData();
			return jsonData || null;
		} catch (error) {
			console.warn("CrossFilterManager: Could not get chart data source:", error);
			return null;
		}
	}

	/**
	 * Extract dimensions from chart data
	 * @param {object} data - Chart data
	 * @returns {string[]} - Array of dimension names
	 */
	extractDimensions(data) {
		const dimensions = new Set(["category", "label"]);

		if (data && Array.isArray(data)) {
			data.forEach(item => {
				if (item.label) dimensions.add("label");
				if (item.category) dimensions.add("category");
				if (item.name) dimensions.add("name");
			});
		}

		if (data && data.categories) {
			dimensions.add("category");
		}

		return Array.from(dimensions);
	}

	/**
	 * Subscribe to filter change events
	 * @param {function} callback - Callback function(filters)
	 * @returns {function} - Unsubscribe function
	 */
	subscribe(callback) {
		this.listeners.add(callback);
		return () => this.listeners.delete(callback);
	}

	/**
	 * Notify all listeners of filter changes
	 */
	notifyListeners() {
		const filterState = Object.fromEntries(this.filters);
		this.listeners.forEach(callback => {
			try {
				callback(filterState, this.sourceChartId, this.crossFilterFieldName, this.crossFilterRawValue);
			} catch (error) {
				console.error("CrossFilterManager: Error in listener callback:", error);
			}
		});
	}

	/**
	 * Get current filter state
	 * @returns {object} - Current filters as object including mode
	 */
	getFilters() {
		return {
			...Object.fromEntries(this.filters),
			highlightMode: this.highlightMode,
			seriesFilter: this.seriesFilter
		};
	}

	/**
	 * Set a series-only filter (highlight all data points of a specific series/legend)
	 * @param {string} seriesName - The series name to highlight
	 * @param {string} sourceChartId - ID of chart that initiated the filter
	 */
	setSeriesFilter(seriesName, sourceChartId = null) {
		if (this.isUpdating) return;
		
		// Check if clicking same series - toggle off
		if (this.seriesFilter === seriesName && !this.filters.has("category")) {
			this.clearFilters();
			return;
		}
		
		this.isUpdating = true;
		this.sourceChartId = sourceChartId;
		this.seriesFilter = seriesName;
		
		// Clear category filter, only use series filter
		this.filters.clear();
		this.filters.set("series", seriesName);

		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout);
		}

		this.debounceTimeout = setTimeout(() => {
			this.resetAllChartsToOriginal();
			setTimeout(() => {
				this.applyFiltersToAllCharts();
				this.notifyListeners();
				this.isUpdating = false;
			}, 10);
		}, 50);
	}

	/**
	 * Get the current series filter value
	 * @returns {string|null}
	 */
	getSeriesFilter() {
		return this.seriesFilter;
	}

	/**
	 * Check if any filters are active
	 * @returns {boolean}
	 */
	hasActiveFilters() {
		return this.filters.size > 0;
	}

	/**
	 * Get list of registered chart IDs
	 * @returns {string[]}
	 */
	getRegisteredCharts() {
		return Array.from(this.chartRegistry.keys());
	}

	/**
	 * Deep clone an object
	 * @param {*} obj - Object to clone
	 * @returns {*} - Cloned object
	 */
	deepClone(obj) {
		if (obj === null || typeof obj !== "object") return obj;
		try {
			return JSON.parse(JSON.stringify(obj));
		} catch (e) {
			return obj;
		}
	}

	/**
	 * Get highlighted data source for a chart based on current filters
	 * @param {object} dataSource - Original data source
	 * @param {string} chartId - Chart ID
	 * @returns {object} - Data source with highlighting applied, or original if no filters
	 */
	getHighlightedDataSource(dataSource, chartId = null) {
		if (!this.hasActiveFilters()) {
			return dataSource;
		}

		const chartInfo = this.chartRegistry.get(chartId);
		if (chartInfo && !chartInfo.enableCrossFilter) {
			return dataSource;
		}

		const isSourceChart = chartId === this.sourceChartId;
		const filterValue = this.filters.get("category");

		if (!filterValue) {
			return dataSource;
		}

		return this.highlightDataSource(dataSource, filterValue, isSourceChart);
	}

	/**
	 * Get the current source chart ID (the chart that initiated the filter)
	 * @returns {string|null}
	 */
	getSourceChartId() {
		return this.sourceChartId;
	}

	/**
	 * Dispose of the manager and clean up
	 */
	dispose() {
		this.chartRegistry.forEach((chartInfo, chartId) => {
			this.unregisterChart(chartId);
		});
		this.chartRegistry.clear();
		this.filters.clear();
		this.listeners.clear();
		this.sourceChartId = null;
		this.seriesFilter = null;
		this.highlightMode = "auto";
		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout);
		}
	}
}

// Singleton instance for global access
const crossFilterManager = new CrossFilterManager();

// Export both the class and singleton
export { CrossFilterManager };
export default crossFilterManager;


